/** Closed technical vocabulary shared by browser and relay. No SDK objects. */
export const diagnosticEvents = new Set([
  "quality",
  "camera.setupStarted",
  "join.started",
  "connected",
  "stopped",
  "connecting",
  "reconnecting",
  "connection.poor",
  "connection.good",
  "connection.suggestStopVideo",
  "attendee.present",
  "attendee.left",
  "playback.bindFailed",
  "microphone.setupStarted",
  "microphone.ready",
  "microphone.setupFailed",
  "leave.requested",
  "microphone.muted",
  "microphone.unmuted",
  "camera.enabled",
  "camera.disabled",
  "screenShare.started",
  "screenShare.stopped",
  "media.cleanup",
  "media.preparationCancelled",
  "media.checked",
  "media.microphone.prepared",
  "media.microphone.failed",
  "media.camera.acquisitionStarted",
  "media.camera.enumerationCompleted",
  "media.camera.inputCompleted",
  "media.camera.prepared",
  "media.camera.failed",
  "media.preparationStarted",
  "media.claimed",
  "meetingStartRequested",
  "meetingStartSucceeded",
  "meetingStartFailed",
  "meetingEnded",
  "meetingFailed",
  "audioInputFailed",
  "videoInputFailed",
  "audioInputSelected",
  "videoInputSelected",
  "deviceLabelTriggerFailed",
  "meetingReconnected",
  "attendeePresenceReceived",
  "audioInputUnselected",
  "videoInputUnselected",
  "signalingDropped",
  "signalingCloseCode",
  "signalingCloseReason",
  "signalingCloseWasClean",
  "receivingAudioDropped",
  "sendingAudioFailed",
  "sendingAudioRecovered",
]);
const metricNames = new Set([
  "statusCode",
  "local",
  "microphoneTrackLive",
  "microphoneTrackEnabled",
  "microphoneTrackMuted",
  "playbackConnected",
  "playbackPaused",
  "playbackMuted",
  "playbackReadyState",

  "videoUpstreamBitrate",
  "videoUpstreamPacketsSent",
  "videoUpstreamPacketLossPercent",
  "videoUpstreamFramesEncodedPerSecond",
  "videoUpstreamFramesInputPerSecond",
  "videoUpstreamFrameHeight",
  "videoUpstreamFrameWidth",
  "videoUpstreamJitterMs",
  "videoUpstreamRoundTripTimeMs",
  "videoUpstreamTotalEncodeTimePerSecond",
  "videoUpstreamCpuQualityLimitationDurationPerSecond",
  "videoDownstreamBitrate",
  "videoDownstreamPacketLossPercent",
  "videoDownstreamPacketsReceived",
  "videoDownstreamFramesDecodedPerSecond",
  "videoDownstreamFrameHeight",
  "videoDownstreamFrameWidth",
  "videoDownstreamJitterMs",
  "videoDownstreamDelayMs",
  "audioPacketsReceived",
  "audioPacketsReceivedFractionLoss",
  "audioDecoderLoss",
  "audioPacketsSent",
  "audioPacketLossPercent",
  "audioUpstreamRoundTripTimeMs",
  "videoPacketSentPerSecond",
  "audioSpeakerDelayMs",
  "audioUpstreamJitterMs",
  "audioDownstreamJitterMs",
  "nackCountReceivedPerSecond",
  "availableOutgoingBitrate",
  "availableIncomingBitrate",
  "currentRoundTripTimeMs",
  "audioDownstreamLevel",
  "audioUpstreamLevel",
]);
/** Camera-only numeric vocabulary. See scripts/calls/README.md for encodings. */
export function isCameraMetric(event: string, key: string, value: number): boolean {
  const events = [
    "media.camera.acquisitionStarted",
    "media.camera.enumerationCompleted",
    "media.camera.inputCompleted",
    "media.camera.prepared",
    "media.camera.failed",
  ];
  if (!events.includes(event) || !Number.isSafeInteger(value) || value < 0) return false;
  if (key === "cameraAcquisitionId") return value > 0;
  if (event === "media.camera.acquisitionStarted") {
    return (key === "permissionState" && value <= 3) || (key === "acquisitionContext" && value <= 1);
  }
  if (key === "durationMs") return value <= 86_400_000;
  return (
    key === "failureCategory" &&
    event !== "media.camera.prepared" &&
    value <= 5 &&
    (event !== "media.camera.failed" || value > 0)
  );
}

export interface DiagnosticRecord {
  time: string;
  call: string;
  participant: string;
  attempt: string;
  event: string;
  elapsedMs: number;
  metrics: Record<string, number>;
}
export function parseDiagnostic(value: unknown): DiagnosticRecord | undefined {
  if (!value || typeof value !== "object") return;
  const v = value as Record<string, unknown>;
  if (
    typeof v.call !== "string" ||
    !/^[a-zA-Z0-9:._-]{1,160}$/.test(v.call) ||
    typeof v.attempt !== "string" ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v.attempt) ||
    !["host", "guest", "local"].includes(v.participant as string) ||
    !diagnosticEvents.has(v.event as string) ||
    typeof v.elapsedMs !== "number" ||
    !Number.isSafeInteger(v.elapsedMs) ||
    v.elapsedMs < 0 ||
    v.elapsedMs > 86_400_000 ||
    !v.metrics ||
    typeof v.metrics !== "object" ||
    Array.isArray(v.metrics)
  )
    return;
  const metrics: Record<string, number> = {};
  const entries = Object.entries(v.metrics);
  if (entries.length > 256) return;
  for (const [key, n] of entries) {
    const name = key.replace(/^stream(?:[0-9]|[12][0-9]|3[01])\./, "");
    if (typeof n === "number" && isCameraMetric(v.event as string, key, n)) {
      metrics[key] = n;
      continue;
    }
    if (v.call === "app-media") continue;
    if (metricNames.has(name) && typeof n === "number" && Number.isFinite(n) && Math.abs(n) <= 1e12) metrics[key] = n;
  }
  return {
    time: new Date().toISOString(),
    call: v.call,
    participant: v.participant as string,
    attempt: v.attempt,
    event: v.event as string,
    elapsedMs: v.elapsedMs,
    metrics,
  };
}
